/**
 * Utility functions for handling images in the application
 */

// Define a constant for the fallback image
export const DEFAULT_DESTINATION_IMAGE = 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?q=80&w=2070';

/**
 * Get a destination image based on the search term
 * This uses a curated list of high-quality travel images from Unsplash
 * 
 * @param {string} destination - The destination name to search for
 * @returns {string} - URL of an image for the destination
 */
export const getDestinationImage = (destination) => {
  // Remove any text after commas for cleaner search
  const cleanDestination = destination.split(',')[0].trim();
  
  // Default fallback images by region/continent
  const fallbackImages = {
    default: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&auto=format&fit=crop',
    europe: 'https://images.unsplash.com/photo-1491557345352-5929e343eb89?w=800&auto=format&fit=crop',
    asia: 'https://images.unsplash.com/photo-1535139262971-c51845709a48?w=800&auto=format&fit=crop',
    africa: 'https://images.unsplash.com/photo-1523805009345-7448845a9e53?w=800&auto=format&fit=crop',
    australia: 'https://images.unsplash.com/photo-1523482580672-f109ba8cb9be?w=800&auto=format&fit=crop',
    'north america': 'https://images.unsplash.com/photo-1485738422979-f5c462d49f74?w=800&auto=format&fit=crop',
    'south america': 'https://images.unsplash.com/photo-1483729558449-99ef09a8c325?w=800&auto=format&fit=crop'
  };
  
  // Map of popular destinations and their images
  const destinationImages = {
    // Europe
    'paris': 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=800&auto=format&fit=crop',
    'london': 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=800&auto=format&fit=crop',
    'rome': 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=800&auto=format&fit=crop',
    'barcelona': 'https://images.unsplash.com/photo-1583422409516-2895a77efded?w=800&auto=format&fit=crop',
    'amsterdam': 'https://images.unsplash.com/photo-1576924542622-772b3a8b5b8f?w=800&auto=format&fit=crop',
    'berlin': 'https://images.unsplash.com/photo-1528728329032-2972f65dfb3f?w=800&auto=format&fit=crop',
    'prague': 'https://images.unsplash.com/photo-1592906209472-a36b1f3782ef?w=800&auto=format&fit=crop',
    'vienna': 'https://images.unsplash.com/photo-1516550893885-985c994edd4a?w=800&auto=format&fit=crop',
    'venice': 'https://images.unsplash.com/photo-1534113414509-0eec2bfb493f?w=800&auto=format&fit=crop',
    'florence': 'https://images.unsplash.com/photo-1543429248-45671ef8abd5?w=800&auto=format&fit=crop',
    'dublin': 'https://images.unsplash.com/photo-1580168268588-695d582b62d4?w=800&auto=format&fit=crop',
    'edinburgh': 'https://images.unsplash.com/photo-1598620617148-c9e8bdded0e0?w=800&auto=format&fit=crop',
    'madrid': 'https://images.unsplash.com/photo-1539037116277-4db20889f2d4?w=800&auto=format&fit=crop',
    'lisbon': 'https://images.unsplash.com/photo-1558370781-d6196949e317?w=800&auto=format&fit=crop',
    'athens': 'https://images.unsplash.com/photo-1503152394-c571994fd383?w=800&auto=format&fit=crop',
    'santorini': 'https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?w=800&auto=format&fit=crop',
    'istanbul': 'https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?w=800&auto=format&fit=crop',
    
    // North America
    'new york': 'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=800&auto=format&fit=crop',
    'san francisco': 'https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=800&auto=format&fit=crop',
    'los angeles': 'https://images.unsplash.com/photo-1580655653885-65763b2597d0?w=800&auto=format&fit=crop',
    'chicago': 'https://images.unsplash.com/photo-1494522855154-9297ac14b55f?w=800&auto=format&fit=crop',
    'miami': 'https://images.unsplash.com/photo-1506966953602-c20cc11f75e3?w=800&auto=format&fit=crop',
    'seattle': 'https://images.unsplash.com/photo-1543731068-7e0f5beff43a?w=800&auto=format&fit=crop',
    'toronto': 'https://images.unsplash.com/photo-1514924962253-b9d020e0cfea?w=800&auto=format&fit=crop',
    'vancouver': 'https://images.unsplash.com/photo-1559511260-66a654ae982a?w=800&auto=format&fit=crop',
    'montreal': 'https://images.unsplash.com/photo-1519178614-68673b201f36?w=800&auto=format&fit=crop',
    'new orleans': 'https://images.unsplash.com/photo-1571893544028-06bfc69c2f8a?w=800&auto=format&fit=crop',
    'boston': 'https://images.unsplash.com/photo-1501979376754-f68c411e9fe6?w=800&auto=format&fit=crop',
    'las vegas': 'https://images.unsplash.com/photo-1605833556294-ea5c7a74f57d?w=800&auto=format&fit=crop',
    'mexico city': 'https://images.unsplash.com/photo-1518659526054-190340b15ae0?w=800&auto=format&fit=crop',
    'havana': 'https://images.unsplash.com/photo-1500759285222-a95626b934cb?w=800&auto=format&fit=crop',
    
    // Asia
    'tokyo': 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=800&auto=format&fit=crop',
    'kyoto': 'https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=800&auto=format&fit=crop',
    'bangkok': 'https://images.unsplash.com/photo-1508009603885-50cf7c579365?w=800&auto=format&fit=crop',
    'singapore': 'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=800&auto=format&fit=crop',
    'hong kong': 'https://images.unsplash.com/photo-1536599018102-9f803c140fc1?w=800&auto=format&fit=crop',
    'beijing': 'https://images.unsplash.com/photo-1577706881850-bf37e9dae633?w=800&auto=format&fit=crop',
    'shanghai': 'https://images.unsplash.com/photo-1538428494232-9c0d8a3ab403?w=800&auto=format&fit=crop',
    'seoul': 'https://images.unsplash.com/photo-1538485399081-7c8973f8011b?w=800&auto=format&fit=crop',
    'dubai': 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=800&auto=format&fit=crop',
    'bali': 'https://images.unsplash.com/photo-1604999333679-b86d54738315?w=800&auto=format&fit=crop',
    'delhi': 'https://images.unsplash.com/photo-1575565243348-061a8bc12d4b?w=800&auto=format&fit=crop',
    'mumbai': 'https://images.unsplash.com/photo-1566552881560-0be862a7c445?w=800&auto=format&fit=crop',
    
    // Australia/Oceania
    'sydney': 'https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=800&auto=format&fit=crop',
    'melbourne': 'https://images.unsplash.com/photo-1514395462725-fb4566210144?w=800&auto=format&fit=crop',
    'brisbane': 'https://images.unsplash.com/photo-1543669483-37ce96e24ca8?w=800&auto=format&fit=crop',
    'auckland': 'https://images.unsplash.com/photo-1507699622108-4be3abd695ad?w=800&auto=format&fit=crop',
    'queenstown': 'https://images.unsplash.com/photo-1591219237603-a3d8650b82f2?w=800&auto=format&fit=crop',
    
    // Africa
    'cape town': 'https://images.unsplash.com/photo-1580060839134-75a5edca2e99?w=800&auto=format&fit=crop',
    'marrakech': 'https://images.unsplash.com/photo-1597212618440-806262de4f6b?w=800&auto=format&fit=crop',
    'cairo': 'https://images.unsplash.com/photo-1572252009286-268acec5ca0a?w=800&auto=format&fit=crop',
    'nairobi': 'https://images.unsplash.com/photo-1605773142275-1a3091e8f75d?w=800&auto=format&fit=crop',
    
    // South America
    'rio de janeiro': 'https://images.unsplash.com/photo-1516306580123-e6e52b1b7b5f?w=800&auto=format&fit=crop',
    'buenos aires': 'https://images.unsplash.com/photo-1612294037635-ec93ede3b9ba?w=800&auto=format&fit=crop',
    'lima': 'https://images.unsplash.com/photo-1531968455063-dec4c503b428?w=800&auto=format&fit=crop',
    'cusco': 'https://images.unsplash.com/photo-1532087855576-8c0ccabf2281?w=800&auto=format&fit=crop',
  };

  // Try to match the destination with our predefined list
  const destinationLower = cleanDestination.toLowerCase();
  
  // Check if we have an exact match
  if (destinationImages[destinationLower]) {
    return destinationImages[destinationLower];
  }
  
  // Check if destination includes any of our keys (for cases like "Paris, France")
  for (const [key, imageUrl] of Object.entries(destinationImages)) {
    if (destinationLower.includes(key)) {
      return imageUrl;
    }
  }
  
  // Return fallback image based on continent if mentioned
  for (const [continent, imageUrl] of Object.entries(fallbackImages)) {
    if (destinationLower.includes(continent)) {
      return imageUrl;
    }
  }
  
  // Return default fallback
  return fallbackImages.default;
};

/**
 * Get an activity image based on the activity type and title
 * This uses a curated list of high-quality images from Unsplash
 * 
 * @param {string} activityType - The type of activity ('restaurant', 'hotel', 'activity', etc.)
 * @param {string} activityTitle - The title of the activity
 * @returns {string} - URL of an image for the activity
 */
export const getActivityImage = (activityType, activityTitle) => {
  if (!activityType && !activityTitle) {
    return 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?q=80&w=2070';
  }
  
  // Default images for different activity types
  const activityTypeImages = {
    'restaurant': 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=2070',
    'hotel': 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?q=80&w=2070',
    'activity': 'https://images.unsplash.com/photo-1541697183324-e05537a363e0?q=80&w=2070',
    'museum': 'https://images.unsplash.com/photo-1499968546898-192c5e2dfc46?q=80&w=2070',
    'shopping': 'https://images.unsplash.com/photo-1482555670981-4de159d8553b?q=80&w=2070',
    'park': 'https://images.unsplash.com/photo-1505245208761-ba872912fac0?q=80&w=2070',
    'beach': 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=2070',
    'landmark': 'https://images.unsplash.com/photo-1558862625-1f6a4b5b7b8f?q=80&w=2070',
    'nightlife': 'https://images.unsplash.com/photo-1571813094386-3a1c81aff381?q=80&w=2070',
    'spa': 'https://images.unsplash.com/photo-1620733723572-11c53f73a416?q=80&w=2070',
    'tour': 'https://images.unsplash.com/photo-1473496169904-658ba7c44d8a?q=80&w=2070',
    'entertainment': 'https://images.unsplash.com/photo-1603190287605-e6ade32fa852?q=80&w=2070',
    'temple': 'https://images.unsplash.com/photo-1609607273484-f044556e41d4?q=80&w=2070',
    'castle': 'https://images.unsplash.com/photo-1583356322882-85559b472f56?q=80&w=2070',
    'market': 'https://images.unsplash.com/photo-1555876484-a71ae182fdb5?q=80&w=2070',
    'garden': 'https://images.unsplash.com/photo-1510356837441-8c8aec569cf2?q=80&w=2070',
    'breakfast': 'https://images.unsplash.com/photo-1533089860892-a7c6f0a88666?q=80&w=2070',
    'lunch': 'https://images.unsplash.com/photo-1576867757603-05b134ebc379?q=80&w=2070',
    'dinner': 'https://images.unsplash.com/photo-1559339352-11d035aa65de?q=80&w=2070',
    'cafe': 'https://images.unsplash.com/photo-1445116572660-236099ec97a0?q=80&w=2070',
    'bar': 'https://images.unsplash.com/photo-1567189022364-40d52f17a3c7?q=80&w=2070',
    'hiking': 'https://images.unsplash.com/photo-1501554728187-ce583db33af7?q=80&w=2070',
    'swimming': 'https://images.unsplash.com/photo-1530549387789-4c1017266635?q=80&w=2070',
    'boating': 'https://images.unsplash.com/photo-1569263900347-06b1e8c825ab?q=80&w=2070',
    'sightseeing': 'https://images.unsplash.com/photo-1500835556837-99ac94a94552?q=80&w=2070',
    'art': 'https://images.unsplash.com/photo-1499781350541-7783f6c6a0c8?q=80&w=2070',
    'history': 'https://images.unsplash.com/photo-1461360370896-14413d3b275d?q=80&w=2070',
    'nature': 'https://images.unsplash.com/photo-1470770841072-f978cf4d019e?q=80&w=2070',
    'architecture': 'https://images.unsplash.com/photo-1487958449943-2429e8be8625?q=80&w=2070',
    'wildlife': 'https://images.unsplash.com/photo-1504173010664-32509aeebb62?q=80&w=2070',
    'photography': 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?q=80&w=2070',
    'relaxation': 'https://images.unsplash.com/photo-1528938102132-4a9276b8e320?q=80&w=2070',
    'adventure': 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?q=80&w=2070',
    'traditional': 'https://images.unsplash.com/photo-1528495612343-9ca9f4a9f67c?q=80&w=2070',
    'local': 'https://images.unsplash.com/photo-1464037866556-6812c9d1c72e?q=80&w=2070',
    'street food': 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=2070',
    'fine dining': 'https://images.unsplash.com/photo-1511018556340-d16986a1c194?q=80&w=2070',
    'cruise': 'https://images.unsplash.com/photo-1548574505-5e239809ee19?q=80&w=2070',
    'sunset': 'https://images.unsplash.com/photo-1495430288918-03be1749bec9?q=80&w=2070',
    'sunrise': 'https://images.unsplash.com/photo-1470252649378-9c29740c9fa8?q=80&w=2070',
    'evening': 'https://images.unsplash.com/photo-1519681393784-d120267933ba?q=80&w=2070',
    'morning': 'https://images.unsplash.com/photo-1470252649378-9c29740c9fa8?q=80&w=2070',
    'check-in': 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?q=80&w=2070',
    'arrival': 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?q=80&w=2070',
    'departure': 'https://images.unsplash.com/photo-1536318733676-5f3e8d074de2?q=80&w=2070',
    'default': 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?q=80&w=2070'
  };

  // Default activity image if no match is found
  let imageUrl = 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?q=80&w=2070';
  
  // First try to match by activity type
  if (activityType && activityTypeImages[activityType.toLowerCase()]) {
    imageUrl = activityTypeImages[activityType.toLowerCase()];
  }
  
  // If we have a title, try to match keywords in the title
  if (activityTitle) {
    const title = activityTitle.toLowerCase();
    
    // Check if any keywords in the title match our image map
    for (const [key, value] of Object.entries(activityTypeImages)) {
      if (title.includes(key.toLowerCase())) {
        return value; // Return immediately if we find a match in the title
      }
    }
  }
  
  return imageUrl;
};

// Get image for a specific location
export const getLocationImage = (location, activityType = '', fallbackDestination = '') => {
  // Common landmarks and their images
  const landmarkImages = {
    // Paris
    'eiffel tower': 'https://images.unsplash.com/photo-1543349689-9a4d426bee8e?w=800&auto=format&fit=crop',
    'louvre': 'https://images.unsplash.com/photo-1572602648934-eb01baccc603?w=800&auto=format&fit=crop',
    'notre dame': 'https://images.unsplash.com/photo-1584807187937-bf723f6a9a7c?w=800&auto=format&fit=crop',
    'champs-élysées': 'https://images.unsplash.com/photo-1542824107-caf6abd3bdc2?w=800&auto=format&fit=crop',
    'montmartre': 'https://images.unsplash.com/photo-1551887373-11fe231284a3?w=800&auto=format&fit=crop',
    
    // London
    'big ben': 'https://images.unsplash.com/photo-1529655683826-aba9b3e77383?w=800&auto=format&fit=crop',
    'british museum': 'https://images.unsplash.com/photo-1515263487990-61b33d8dbb57?w=800&auto=format&fit=crop',
    'tower bridge': 'https://images.unsplash.com/photo-1592509255531-161181e0cb8d?w=800&auto=format&fit=crop',
    'buckingham palace': 'https://images.unsplash.com/photo-1552036268-b843a356d2e4?w=800&auto=format&fit=crop',
    
    // Rome
    'colosseum': 'https://images.unsplash.com/photo-1604580864964-0462f5d5b1a8?w=800&auto=format&fit=crop',
    'vatican': 'https://images.unsplash.com/photo-1531572753322-ad063cecc140?w=800&auto=format&fit=crop',
    'trevi fountain': 'https://images.unsplash.com/photo-1525874684015-58379d421a52?w=800&auto=format&fit=crop',
    'spanish steps': 'https://images.unsplash.com/photo-1505557515754-f430072674ab?w=800&auto=format&fit=crop',
    
    // New York
    'times square': 'https://images.unsplash.com/photo-1524729429516-485db0307e59?w=800&auto=format&fit=crop',
    'central park': 'https://images.unsplash.com/photo-1534804559744-5595122132b6?w=800&auto=format&fit=crop',
    'statue of liberty': 'https://images.unsplash.com/photo-1546059364-1f31f2a2f7c3?w=800&auto=format&fit=crop',
    'empire state': 'https://images.unsplash.com/photo-1582049929890-ca834967de56?w=800&auto=format&fit=crop',
    'brooklyn bridge': 'https://images.unsplash.com/photo-1522775559573-2f76d540932b?w=800&auto=format&fit=crop',
    
    // Tokyo
    'shibuya': 'https://images.unsplash.com/photo-1573455494060-c5595004fb6c?w=800&auto=format&fit=crop',
    'tokyo tower': 'https://images.unsplash.com/photo-1536098561742-ca998e48cbcc?w=800&auto=format&fit=crop',
    'meiji shrine': 'https://images.unsplash.com/photo-1521716262372-4b8da12c7e39?w=800&auto=format&fit=crop',
    
    // Generic landmarks
    'beach': 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&auto=format&fit=crop',
    'castle': 'https://images.unsplash.com/photo-1590059300640-40ae11ca0641?w=800&auto=format&fit=crop',
    'palace': 'https://images.unsplash.com/photo-1596636478939-6bf8dba7ca4e?w=800&auto=format&fit=crop',
    'cathedral': 'https://images.unsplash.com/photo-1558244398-4a7253951edb?w=800&auto=format&fit=crop',
    'museum': 'https://images.unsplash.com/photo-1626615160823-01f52fd892c9?w=800&auto=format&fit=crop',
    'park': 'https://images.unsplash.com/photo-1519331379826-f10be5486c6f?w=800&auto=format&fit=crop',
    'garden': 'https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=800&auto=format&fit=crop',
    'temple': 'https://images.unsplash.com/photo-1550022596-21c4310d25f5?w=800&auto=format&fit=crop',
    'restaurant': 'https://images.unsplash.com/photo-1559339352-11d035aa65de?w=800&auto=format&fit=crop',
  };

  // Try to find landmark image based on location name
  const locationLower = location.toLowerCase();
  
  // Direct lookup
  if (landmarkImages[locationLower]) {
    return landmarkImages[locationLower];
  }
  
  // Check if location includes any landmarks
  for (const [landmark, imageUrl] of Object.entries(landmarkImages)) {
    if (locationLower.includes(landmark)) {
      return imageUrl;
    }
  }
  
  // Fall back to activity type image
  if (activityType) {
    return getActivityImage(activityType, location);
  }
  
  // Fall back to destination image
  if (fallbackDestination) {
    return getDestinationImage(fallbackDestination);
  }
  
  // Return a generic travel image if nothing else matches
  return 'https://images.unsplash.com/photo-1488085061387-422e29b40080?w=800&auto=format&fit=crop';
}; 